(function () {
  'use strict';

  var api = window.ModobloomMaterialStory;
  if (api && typeof api.schedule === 'function') {
    api.schedule();
    return;
  }

  var story = null;
  var sources = [];
  var activeIndex = -1;
  var scrollRaf = 0;
  var buildRaf = 0;
  var layoutRaf = 0;
  var transitionTimer = 0;
  var transitionToken = 0;
  var pendingIndex = -1;
  var resizeObserver = null;
  var hasEnteredStory = false;

  var accents = ['#dcebc5', '#cfe9ee', '#f5b8c5', '#f6deb1', '#9fa8ea'];

  function updateLayoutMetrics() {
    layoutRaf = 0;
    if (!story) return;

    var titles = story.querySelector('.material-story__titles');
    var activeCopy = story.querySelector('.material-story__panel.is-active .material-story__copy');
    var activeMedia = story.querySelector('.material-story__panel.is-active .material-story__media');
    var viewport = story.querySelector('.material-story__viewport');

    if (titles) {
      story.style.setProperty(
        '--material-story-mobile-visible-titles-height',
        Math.ceil(titles.getBoundingClientRect().height) + 'px'
      );
    }

    if (activeCopy) {
      story.style.setProperty(
        '--material-story-mobile-copy-height',
        Math.ceil(activeCopy.getBoundingClientRect().height) + 'px'
      );
    }

    if (activeMedia && activeCopy && titles && viewport) {
      var storyStyles = window.getComputedStyle(story);
      var firstTitle = titles.querySelector('[data-story-title]');
      var titleRowHeight = firstTitle ? firstTitle.offsetHeight : 24;
      var titleGap = Number.parseFloat(
        storyStyles.getPropertyValue('--material-story-mobile-title-gap')
      ) || 8;
      var visibleTitleBottom = titles.offsetTop + titleRowHeight * 3 + titleGap * 2;
      var contentTop = activeCopy.offsetTop;
      var availableSpaceCenter = (visibleTitleBottom + contentTop) / 2;
      var mediaCenterOffset = (
        availableSpaceCenter -
        (activeMedia.offsetTop + activeMedia.offsetHeight / 2)
      );
      story.style.setProperty(
        '--material-story-mobile-image-center-offset-y',
        mediaCenterOffset + 'px'
      );
    }
  }

  function requestLayoutMetrics() {
    if (!layoutRaf) layoutRaf = window.requestAnimationFrame(updateLayoutMetrics);
  }

  function getWrapper(source) {
    return source.closest('.shopify-section') || source.parentElement;
  }

  function updateSelection(nextIndex) {
    if (!story) return;

    story.style.setProperty('--material-story-accent', accents[nextIndex] || accents[0]);
    story.style.setProperty('--material-story-index', String(nextIndex));
    var isMobile = window.matchMedia('(max-width: 749px)').matches;

    story.querySelectorAll('[data-story-title], [data-story-icon]').forEach(function (button) {
      var itemIndex = Number(button.dataset.index);
      var isActive = itemIndex === nextIndex;
      if (button.hasAttribute('data-story-title')) {
        var activePosition = itemIndex - nextIndex;
        var storyPosition = activePosition;
        var distance = Math.abs(activePosition);
        var isVisible = isMobile
          ? (nextIndex === 0 ? distance <= 2 : distance <= 1)
          : distance <= 2;
        var titleOpacity = distance === 0 ? 1 : distance === 1 ? 0.55 : distance === 2 ? 0.25 : 0;

        button.classList.toggle('is-near', isVisible);
        button.classList.remove('is-wrapping');
        button.dataset.activeDistance = String(distance);
        button.dataset.storyPosition = String(storyPosition);
        button.dataset.storyMobileVisible = isVisible ? 'true' : 'false';
        button.style.setProperty('--material-story-title-position', String(storyPosition));
        button.style.setProperty('--material-story-title-opacity', String(titleOpacity));
      }
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    story.style.setProperty('--material-story-desktop-title-shift', '0px');

    requestLayoutMetrics();
  }

  function seekIconVideoEndFrame(video) {
    if (!video) return;

    var endFrameToken = String(Date.now()) + '-' + String(transitionToken);
    video.dataset.videoEndFrameToken = endFrameToken;

    var seek = function () {
      if (video.dataset.videoEndFrameToken !== endFrameToken) return;
      if (video.dataset.videoPlaying !== 'false') return;

      var duration = Number(video.duration);
      if (!Number.isFinite(duration) || duration <= 0) return;

      try {
        video.currentTime = Math.max(0, duration - Math.min(0.08, duration / 2));
      } catch (error) {
        return;
      }
    };

    if (video.readyState >= 1) {
      seek();
      return;
    }

    video.addEventListener('loadedmetadata', seek, { once: true });
    video.load();
  }

  function restoreIconVideo(video) {
    if (!video) return;

    video.dataset.videoReplayToken = '';
    video.dataset.videoPlaying = 'false';
    video.pause();
    video.hidden = false;
    seekIconVideoEndFrame(video);
  }

  function restoreAllIconVideos() {
    if (!story) return;
    story.querySelectorAll('[data-story-icon] video[data-icon-video]').forEach(restoreIconVideo);
  }

  function replayIconMedia(nextIndex) {
    if (!story) return;

    restoreAllIconVideos();
    var activeButton = story.querySelector('[data-story-icon][data-index="' + nextIndex + '"]');
    var video = activeButton && activeButton.querySelector('video[data-icon-video]');

    if (!video) return;

    var replayToken = String(Date.now()) + '-' + String(transitionToken);
    var videoScale = Math.max(1, Math.min(4.5, Number(video.dataset.videoScale) || 1));

    video.dataset.videoReplayToken = replayToken;
    video.dataset.videoEndFrameToken = '';
    video.dataset.videoPlaying = 'loading';
    video.style.setProperty('--material-story-icon-scale', String(videoScale));
    video.hidden = false;

    try {
      video.currentTime = 0;
    } catch (error) {
      video.load();
    }

    var playPromise = video.play();
    if (!playPromise || typeof playPromise.then !== 'function') {
      video.dataset.videoPlaying = 'true';
      return;
    }

    playPromise
      .then(function () {
        if (video.dataset.videoReplayToken === replayToken) {
          video.dataset.videoPlaying = 'true';
        }
      })
      .catch(function () {
        if (video.dataset.videoReplayToken !== replayToken) return;
        restoreIconVideo(video);
      });
  }

  function commitActive(nextIndex, shouldAnimateIn) {
    if (!story) return;

    var previousIndex = activeIndex;
    var incomingPanel = story.querySelector('[data-story-panel][data-index="' + nextIndex + '"]');

    if (incomingPanel && shouldAnimateIn) {
      incomingPanel.classList.add('is-entering');
    }

    story.querySelectorAll('[data-story-panel]').forEach(function (panel, index) {
      var isActive = index === nextIndex;
      panel.classList.remove('is-leaving');
      panel.classList.toggle('is-active', isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    activeIndex = nextIndex;
    pendingIndex = -1;
    story.dataset.activeIndex = String(nextIndex);
    updateSelection(nextIndex);
    if (previousIndex >= 0 && previousIndex !== nextIndex) {
      replayIconMedia(nextIndex);
    } else {
      restoreAllIconVideos();
    }

    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () {
        if (!story) return;
        if (incomingPanel) incomingPanel.classList.remove('is-entering');
        story.classList.remove('is-switching');
        story.removeAttribute('aria-busy');
      });
    });
  }

  function setActive(nextIndex) {
    if (!story) return;

    var count = Number(story.dataset.count || 0);
    var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!count) return;
    nextIndex = Math.max(0, Math.min(count - 1, nextIndex));

    if (nextIndex === pendingIndex) return;
    if (nextIndex === activeIndex && pendingIndex < 0) {
      updateSelection(nextIndex);
      return;
    }

    transitionToken += 1;
    var token = transitionToken;
    pendingIndex = nextIndex;

    if (transitionTimer) {
      window.clearTimeout(transitionTimer);
      transitionTimer = 0;
    }

    if (reducedMotion || activeIndex < 0) {
      commitActive(nextIndex, false);
      return;
    }

    var outgoingPanel = story.querySelector('[data-story-panel].is-active');
    story.classList.add('is-switching');
    story.setAttribute('aria-busy', 'true');
    story.dataset.targetIndex = String(nextIndex);
    if (outgoingPanel) outgoingPanel.classList.add('is-leaving');

    transitionTimer = window.setTimeout(function () {
      transitionTimer = 0;
      if (!story || token !== transitionToken) return;
      commitActive(nextIndex, true);
    }, 190);
  }

  function scrollToIndex(index) {
    if (!story) return;

    var count = Number(story.dataset.count || 0);
    if (!count) return;
    index = Math.max(0, Math.min(count - 1, index));
    var shouldReplayImmediately = index === activeIndex && pendingIndex < 0;
    setActive(index);
    if (shouldReplayImmediately) replayIconMedia(index);
  }

  function selectIndex(index) {
    if (!story) return;
    setActive(index);
  }

  function updateNavigationOffset() {
    if (!story) return;

    var navigation = document.querySelector('.modo-nav');
    var navigationBottom = 0;

    if (navigation) {
      var navigationRect = navigation.getBoundingClientRect();
      var navigationStyles = window.getComputedStyle(navigation);
      var isOverlayNavigation = navigationStyles.position === 'fixed' || navigationStyles.position === 'sticky';
      var isVisible = navigationStyles.display !== 'none' && navigationStyles.visibility !== 'hidden';

      if (isOverlayNavigation && isVisible && navigationRect.bottom > 0) {
        navigationBottom = Math.max(0, navigationRect.bottom);
      }
    }

    story.style.setProperty('--material-story-navigation-bottom', navigationBottom + 'px');
  }

  function updateFromScroll() {
    scrollRaf = 0;
    if (!story) return;

    updateNavigationOffset();
    requestLayoutMetrics();

    var rect = story.getBoundingClientRect();
    var isStoryVisible = rect.top <= window.innerHeight * 0.55 && rect.bottom >= window.innerHeight * 0.45;

    if (!isStoryVisible) {
      hasEnteredStory = false;
      return;
    }

    if (!hasEnteredStory) {
      hasEnteredStory = true;
      if (activeIndex >= 0 && pendingIndex < 0) replayIconMedia(activeIndex);
    }
  }

  function requestScrollUpdate() {
    if (!scrollRaf) scrollRaf = window.requestAnimationFrame(updateFromScroll);
  }

  function createButton(type, index, label, panelId, accent) {
    var button = document.createElement('button');
    button.type = 'button';
    button.dataset.index = String(index);
    button.setAttribute('role', 'tab');
    button.setAttribute('aria-controls', panelId);
    button.setAttribute('aria-label', label);
    button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
    button.setAttribute('tabindex', index === 0 ? '0' : '-1');
    button.style.setProperty('--item-accent', accent);

    if (type === 'title') {
      button.className = 'material-story__title';
      button.dataset.storyTitle = '';
      var titleText = document.createElement('span');
      titleText.className = 'material-story__title-text';
      titleText.textContent = label;
      button.appendChild(titleText);
    } else {
      button.className = 'material-story__icon-button';
      button.dataset.storyIcon = '';
    }

    button.addEventListener('click', function () {
      var shouldReplayImmediately = index === activeIndex && pendingIndex < 0;
      selectIndex(index);
      if (shouldReplayImmediately) replayIconMedia(index);
    });

    button.addEventListener('keydown', function (event) {
      var count = Number(story && story.dataset.count || 0);
      var next = null;

      if ((event.key === 'ArrowRight' || event.key === 'ArrowDown') && index < count - 1) next = index + 1;
      if ((event.key === 'ArrowLeft' || event.key === 'ArrowUp') && index > 0) next = index - 1;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = count - 1;

      if (next !== null) {
        event.preventDefault();
        selectIndex(next);
        window.requestAnimationFrame(function () {
          var target = story.querySelector('[data-story-' + type + '][data-index="' + next + '"]');
          if (target) target.focus({ preventScroll: true });
        });
      }
    });

    return button;
  }

  function attachSwipeNavigation(viewport) {
    var touchStartX = 0;
    var touchStartY = 0;
    var touchStartedAt = 0;

    function startSwipe(clientX, clientY) {
      touchStartX = clientX;
      touchStartY = clientY;
      touchStartedAt = window.performance.now();
    }

    function finishSwipe(clientX, clientY) {
      if (!touchStartedAt) return;

      var deltaX = clientX - touchStartX;
      var deltaY = clientY - touchStartY;
      touchStartedAt = 0;

      if (Math.abs(deltaX) < 48 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;

      var count = Number(story && story.dataset.count || 0);
      var direction = deltaX < 0 ? 1 : -1;
      var nextIndex = Math.max(0, Math.min(count - 1, activeIndex + direction));

      if (nextIndex !== activeIndex) selectIndex(nextIndex);
    }

    if ('PointerEvent' in window) {
      viewport.addEventListener('pointerdown', function (event) {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        startSwipe(event.clientX, event.clientY);
      }, { passive: true });

      viewport.addEventListener('pointerup', function (event) {
        finishSwipe(event.clientX, event.clientY);
      }, { passive: true });

      viewport.addEventListener('pointercancel', function () {
        touchStartedAt = 0;
      }, { passive: true });
      return;
    }

    viewport.addEventListener('touchstart', function (event) {
      if (event.touches.length !== 1) return;
      startSwipe(event.touches[0].clientX, event.touches[0].clientY);
    }, { passive: true });

    viewport.addEventListener('touchend', function (event) {
      if (event.changedTouches.length !== 1) return;
      finishSwipe(event.changedTouches[0].clientX, event.changedTouches[0].clientY);
    }, { passive: true });
  }

  function teardown() {
    if (transitionTimer) {
      window.clearTimeout(transitionTimer);
      transitionTimer = 0;
    }
    transitionToken += 1;
    pendingIndex = -1;

    restoreAllIconVideos();

    if (resizeObserver) {
      resizeObserver.disconnect();
      resizeObserver = null;
    }

    if (story) {
      story.remove();
      story = null;
    }

    sources.forEach(function (source) {
      var wrapper = getWrapper(source);
      if (!wrapper) return;
      wrapper.hidden = false;
      wrapper.classList.remove('material-story__legacy-source');
      wrapper.removeAttribute('aria-hidden');
    });

    sources = [];
    activeIndex = -1;
    hasEnteredStory = false;
  }

  function build() {
    buildRaf = 0;
    var nextSources = Array.prototype.slice.call(
      document.querySelectorAll('[data-material-story-source]')
    );

    var featureNodes = [];
    nextSources.forEach(function (source) {
      source.querySelectorAll('.craft-feature').forEach(function (feature) {
        featureNodes.push(feature);
      });
    });

    if (featureNodes.length < 2) return;

    teardown();
    sources = nextSources;

    var root = document.createElement('section');
    var sticky = document.createElement('div');
    var viewport = document.createElement('div');
    var titles = document.createElement('div');
    var panels = document.createElement('div');
    var icons = document.createElement('div');

    root.className = 'material-story';
    root.id = 'material-story';
    root.dataset.materialStoryRoot = '';
    root.style.cssText += nextSources[0].getAttribute('style') || '';
    root.dataset.count = String(featureNodes.length);
    root.style.setProperty('--material-story-count', String(featureNodes.length));
    var mobileCopyBottom = Number(nextSources[0] && nextSources[0].dataset.storyMobileCopyBottom);
    if (Number.isFinite(mobileCopyBottom)) {
      root.style.setProperty('--material-story-mobile-copy-bottom', mobileCopyBottom + 'px');
    }
    root.setAttribute('aria-label', 'Material and engineering highlights');

    sticky.className = 'material-story__sticky';
    viewport.className = 'material-story__viewport';
    titles.className = 'material-story__titles';
    titles.setAttribute('role', 'tablist');
    titles.setAttribute('aria-label', 'Feature titles');
    panels.className = 'material-story__panels';
    icons.className = 'material-story__icons';
    icons.setAttribute('role', 'tablist');
    icons.setAttribute('aria-label', 'Switch material feature');

    featureNodes.forEach(function (feature, index) {
      var sourceTitle = feature.querySelector('.craft-feature__title');
      var sourceBody = feature.querySelector('.craft-feature__body');
      var sourceImage = feature.querySelector('.craft-feature__image-stage');
      var sourceVideoTemplate = feature.querySelector('template[data-icon-video-template]');
      var iconVideoScale = Number(feature.dataset.iconVideoScale) || 1;
      var title = sourceTitle ? sourceTitle.textContent.trim() : 'Feature ' + (index + 1);
      var body = sourceBody ? sourceBody.textContent.trim() : '';
      var accent = accents[index] || accents[accents.length - 1];
      var panelId = 'material-story-panel-' + index;
      var panel = document.createElement('article');
      var copy = document.createElement('div');
      var bodyElement = document.createElement('div');
      var media = document.createElement('div');
      var titleButton = createButton('title', index, title, panelId, accent);
      var iconButton = createButton('icon', index, title, panelId, accent);
      iconButton.style.cssText += feature.getAttribute('style') || '';

      panel.id = panelId;
      panel.className = 'material-story__panel' + (index === 0 ? ' is-active' : '');
      panel.dataset.storyPanel = '';
      panel.dataset.index = String(index);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');
      panel.style.cssText += feature.getAttribute('style') || '';

      copy.className = 'material-story__copy';
      bodyElement.className = 'craft-feature__body';
      bodyElement.textContent = body;
      copy.appendChild(bodyElement);

      media.className = 'material-story__media';
      if (sourceImage) {
        var imageClone = sourceImage.cloneNode(true);
        var image = imageClone.querySelector('img');
        if (image) {
          image.draggable = false;
          image.addEventListener('load', requestLayoutMetrics, { once: true });
          if (index === 0) {
            image.loading = 'eager';
            image.fetchPriority = 'high';
          }
        }
        media.appendChild(imageClone);
      }

      if (sourceVideoTemplate) {
        var sourceVideo = sourceVideoTemplate.content.querySelector('video');
        if (sourceVideo) {
          var iconVideo = sourceVideo.cloneNode(true);
          iconVideo.removeAttribute('autoplay');
          iconVideo.removeAttribute('poster');
          iconVideo.autoplay = false;
          iconVideo.controls = false;
          iconVideo.defaultMuted = true;
          iconVideo.loop = false;
          iconVideo.muted = true;
          iconVideo.playsInline = true;
          iconVideo.preload = 'auto';
          iconVideo.hidden = false;
          iconVideo.dataset.iconVideo = '';
          iconVideo.dataset.videoReady = iconVideo.readyState >= 2 ? 'true' : 'false';
          iconVideo.dataset.videoScale = String(iconVideoScale);
          iconVideo.dataset.videoPlaying = 'false';
          iconVideo.dataset.videoReplayToken = '';
          iconVideo.dataset.videoEndFrameToken = '';
          iconVideo.style.setProperty('--material-story-icon-scale', String(iconVideoScale));
          iconVideo.addEventListener('loadeddata', function () {
            this.dataset.videoReady = 'true';
          });
          iconVideo.addEventListener('ended', function () {
            this.dataset.videoPlaying = 'false';
            seekIconVideoEndFrame(this);
          });
          iconButton.appendChild(iconVideo);
        }
      }

      panel.appendChild(copy);
      panel.appendChild(media);
      panels.appendChild(panel);
      titles.appendChild(titleButton);
      icons.appendChild(iconButton);

      if (index < featureNodes.length - 1) {
        var iconDivider = document.createElement('span');
        iconDivider.className = 'material-story__icon-divider';
        iconDivider.setAttribute('role', 'presentation');
        iconDivider.setAttribute('aria-hidden', 'true');
        icons.appendChild(iconDivider);
      }
    });

    viewport.appendChild(titles);
    viewport.appendChild(panels);
    viewport.appendChild(icons);
    attachSwipeNavigation(viewport);
    sticky.appendChild(viewport);
    root.appendChild(sticky);

    var firstWrapper = getWrapper(sources[0]);
    if (!firstWrapper || !firstWrapper.parentNode) return;
    firstWrapper.parentNode.insertBefore(root, firstWrapper);

    if (window.location.hash === '#material-story') {
      window.requestAnimationFrame(function () {
        root.scrollIntoView({ block: 'start' });
      });
    }

    sources.forEach(function (source) {
      var wrapper = getWrapper(source);
      if (!wrapper) return;
      wrapper.hidden = true;
      wrapper.classList.add('material-story__legacy-source');
      wrapper.setAttribute('aria-hidden', 'true');
    });

    story = root;
    activeIndex = -1;
    setActive(0);
    requestScrollUpdate();

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(requestScrollUpdate);
      resizeObserver.observe(story);
      story.querySelectorAll('.craft-feature__image, .craft-feature__placeholder').forEach(function (image) {
        resizeObserver.observe(image);
      });
    }
  }

  function schedule() {
    if (buildRaf) window.cancelAnimationFrame(buildRaf);
    buildRaf = window.requestAnimationFrame(build);
  }

  window.ModobloomMaterialStory = {
    schedule: schedule,
    rebuild: schedule,
    goTo: scrollToIndex
  };

  window.addEventListener('scroll', requestScrollUpdate, { passive: true });
  window.addEventListener('resize', requestScrollUpdate, { passive: true });
  window.addEventListener('pageshow', schedule);
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:unload', schedule);
  document.addEventListener('shopify:section:reorder', schedule);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
})();
